import { Button, ButtonGroup, ColumnHeader, Icon, Spinner, Tooltip, TooltipContent, TooltipTrigger } from "@qw/ui";
import { MiniAppToggle } from "@/app/mini-app-toggle";
import { useConversation } from "@/providers/use-conversation";

import { useInboxQueryStates } from "../providers/inbox-query-states-provider";
import { ConversationInsuredButton } from "./conversation-insured-button";
import { ConversationMenu } from "./conversation-menu";
import { ConversationStarButton } from "./conversation-star-button";

export const ConversationHeader = ({
  canShowInboxBesideMessage = true,
  onShowInbox,
  showBackButton = false,
}: {
  canShowInboxBesideMessage?: boolean;
  onShowInbox?: () => void;
  showBackButton?: boolean;
}) => {
  const { inboxConversation, isReadOnly, loading } = useConversation();
  const { conversationId } = useInboxQueryStates();

  const header = inboxConversation ? inboxConversation.subject || "(No Subject)" : null;

  return (
    <ColumnHeader icon={<ConversationBackButton showBackButton={showBackButton} />}>
      {showBackButton && canShowInboxBesideMessage && onShowInbox && (
        <ShowInboxBesideMessageButton onShowInbox={onShowInbox} />
      )}
      <h4 className="flex-auto font-medium text-base truncate">{header}</h4>
      {showBackButton && loading && <Spinner className="ml-2 text-sm" />}
      <ButtonGroup className="flex-nowrap gap-0 pl-2">
        {(inboxConversation || (conversationId && loading)) && <MiniAppToggle />}
        {!isReadOnly && <ConversationInsuredButton />}
        {!isReadOnly && <ConversationStarButton />}
        <ConversationMenu />
      </ButtonGroup>
    </ColumnHeader>
  );
};

const ShowInboxBesideMessageButton = ({ onShowInbox }: { onShowInbox: () => void }) => (
  <Tooltip>
    <TooltipTrigger asChild>
      <Button
        aria-label="Show list beside message"
        className="mr-1 flex-none"
        variant="ghost"
        size="sm"
        display="icon"
        onClick={onShowInbox}
      >
        <Icon icon="splitscreen_left" />
      </Button>
    </TooltipTrigger>
    <TooltipContent>Show list beside message</TooltipContent>
  </Tooltip>
);

const ConversationBackButton = ({ showBackButton }: { showBackButton: boolean }) => {
  const { setConversationId } = useInboxQueryStates();
  const { loading } = useConversation();

  if (showBackButton) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            aria-label="Back to list"
            variant="ghost"
            size="sm"
            display="icon"
            onClick={() => setConversationId(null)}
          >
            <Icon icon="arrow_back" />
          </Button>
        </TooltipTrigger>
        <TooltipContent>Back to list</TooltipContent>
      </Tooltip>
    );
  }

  if (loading) {
    return <Spinner />;
  }

  return <Icon icon="mail" />;
};
